import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faCircleInfo,
  faClipboardCheck,
  faEye,
  faEyeSlash,
  faFileExcel,
  faFilePdf,
  faFolderOpen,
  faGripDotsVertical,
  faSliders,
  faUsers,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  BicButtonComponent,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicToggleComponent,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {
  buildDefaultReportConfigForRuleset,
  createReportLevelsFromOrder,
  defaultPerspectiveForRulesetType,
  effectiveCheckMode,
  isReportColumnDraggable,
  isReportColumnVisibilityLocked,
  isReportLevelReorderable,
  LevelOrdering,
  migrateLevelsToRuleset,
  MyCheckBuilderState,
  normalizeIdsReportConfigLabels,
  orderingValueFromLevels,
  outputParamsToDetailColumns,
  reconcileLevelColumns,
  refreshKiDetailColumns,
  ReportColumnConfig,
  ReportPerspective,
  RulesetReportConfig,
  STRUCTURE_IDENTIFIKATOR_KEY,
  TASK_CREATION_LEVEL_OPTIONS,
  TaskCreationLevel,
  validLevelOrderings,
} from '../my-checks-builder.model';
import {RulesetService} from '../../../../services/ruleset.service';
import {CheckMode, Ruleset, SetEingabeparameter} from '../../../ruleManagement/models/rule-management.models';
import {ProjectControllerService} from '../../../../api';
import {UserSimpleDto} from '../../../../api/model/userSimpleDto';

@Component({
  selector: 'my-checks-builder-report-step',
  standalone: true,
  imports: [
    FormsModule,
    FaIconComponent,
    CdkDropList,
    CdkDrag,
    BicToggleComponent,
    BicInputComponent,
    BicSelectComponent,
    BicDropdownElementComponent,
    BicButtonComponent,
    BicPopupComponent,
    TooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './builder-report-step.component.html',
})
export class BuilderReportStepComponent implements OnInit {
  @Input() state!: MyCheckBuilderState;
  /**
   * Projekt des Checks. **`undefined` im Admin-Modus** (zentral bereitgestellte Kopie, projektlos) –
   * projektgebundene Daten (Dokumentklassen, Projektnutzer) werden dann nicht geladen und die
   * zugehörigen Felder ausgeblendet. Regelsets werden mit `undefined` von sich aus nur global geladen.
   */
  @Input() projectId?: number;
  @Output() stateChange = new EventEmitter<Partial<MyCheckBuilderState>>();

  faFileExcel = faFileExcel;
  faFilePdf = faFilePdf;
  faClipboardCheck = faClipboardCheck;
  faFolderOpen = faFolderOpen;
  faUsers = faUsers;
  faSliders = faSliders;
  faCircleInfo = faCircleInfo;
  faGripDotsVertical = faGripDotsVertical;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faXmark = faXmark;

  taskLevelOptions = TASK_CREATION_LEVEL_OPTIONS;

  /** Platzhalter-Token für das Dateiname-Muster der Berichtsablage */
  fileNameTokens = ['{Checkname}', '{myCheckname}', '{Prüfregelname}', '{Projekt}', '{Dateiname}', '{Datum}'];

  prebuiltRulesets: Ruleset[] = [];
  customRulesets: Ruleset[] = [];
  /** Projektmitglieder für die E-Mail-Benachrichtigung */
  projectMembers: UserSimpleDto[] = [];
  loading = true;

  /** Index des aktiven Regelset-Tabs in der Berichtsstruktur */
  activeRulesetIndex = 0;

  /** Popup "Anzeigewerte im Bericht anpassen" */
  showColumnEditor = false;
  /** Arbeitskopie der Konfiguration während der Bearbeitung im Popup */
  editingConfig: RulesetReportConfig | null = null;
  /** Schlüssel der aktuell im Popup bearbeiteten Ebene */
  editingLevelKey = '';

  private rulesetService = inject(RulesetService);
  private projectControllerService = inject(ProjectControllerService);

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Wie im Regelauswahl-Schritt: ohne Projekt nur zentral bereitgestellte Regelwerke. Die Liste
    // trägt hier den Tab-Strip der Berichtskonfiguration – ohne sie bleibt der Schritt leer.
    const rulesets$ = this.projectId
      ? this.rulesetService.listRulesets(this.projectId)
      : this.rulesetService.listGlobalRulesetsForSelection();

    rulesets$.subscribe({
      next: (result) => {
        this.prebuiltRulesets = result.prebuilt;
        this.customRulesets = result.custom;
        this.loading = false;
        this.ensureDefaultPerspectiveConfigs();
      },
      error: () => {
        this.loading = false;
      },
    });

    if (this.projectId) {
      this.projectControllerService.getUsers(this.projectId).subscribe({
        next: (users) => (this.projectMembers = users ?? []),
        error: () => (this.projectMembers = []),
      });
    }
  }

  /**
   * Seedet fehlende Berichtskonfigurationen mit der typabhängigen Default-Perspektive,
   * damit der vorausgewählte Wert nicht nur angezeigt, sondern auch im State persistiert
   * und beim Speichern in das Manifest übernommen wird. Bestehende Konfigurationen
   * (vom Nutzer angepasst oder geladen) bleiben inhaltlich unverändert – lediglich eine
   * für die aktuelle Prüfebene ungültige Ebenen-Reihenfolge wird migriert (siehe
   * {@link migrateConfigsToPruefebene}).
   */
  private ensureDefaultPerspectiveConfigs(): void {
    const sets = this.reportRulesets;
    const labelNormalizedConfigs = normalizeIdsReportConfigLabels(this.state.reportConfigs ?? []);
    const detailNormalizedConfigs = refreshKiDetailColumns(labelNormalizedConfigs, this.state.aiOutputParameters);
    const normalizedConfigs = this.migrateConfigsToPruefebene(detailNormalizedConfigs, sets);
    const hasNormalizedChanges = normalizedConfigs !== this.state.reportConfigs;
    if (sets.length === 0) {
      if (hasNormalizedChanges) {
        this.stateChange.emit({reportConfigs: normalizedConfigs});
      }
      return;
    }
    const existingIds = new Set(normalizedConfigs.map(c => c.rulesetId));
    const missing = sets.filter(s => !existingIds.has(s.id));
    if (missing.length === 0) {
      if (hasNormalizedChanges) {
        this.stateChange.emit({reportConfigs: normalizedConfigs});
      }
      return;
    }
    const added = missing.map(s => this.getConfig(s));
    this.stateChange.emit({reportConfigs: [...normalizedConfigs, ...added]});
  }

  /**
   * Schreibt die Prüfebenen-Migration in den State zurück.
   *
   * Die Ebenen-Reihenfolge (3 vs. 4 Spalten) hängt an der Prüfebene des Regelsets. Ändert sich
   * diese NACH dem Anlegen eines myChecks, ist die gespeicherte Reihenfolge ungültig – etwa
   * `files>rules>detail`, obwohl das Set inzwischen Positionen liefert. {@link getConfig} hat
   * das bislang nur beim Lesen korrigiert: die Anzeige zeigte die migrierte Reihenfolge, der
   * State (und damit das gespeicherte Manifest) behielt die alte. Erneutes Speichern hat den
   * Check deshalb nicht repariert. Die Migration muss also hier passieren, wo sie in den State
   * zurückfließt. Unverändert bleibt die Array-Identität, wenn nichts zu migrieren ist – nur
   * dann bleibt `hasNormalizedChanges` aussagekräftig.
   */
  private migrateConfigsToPruefebene(
    configs: RulesetReportConfig[],
    sets: Ruleset[],
  ): RulesetReportConfig[] {
    let changed = false;
    const migrated = configs.map(config => {
      // Unbekanntes Set (noch nicht geladen / nicht mehr ausgewählt): Prüfebene unbekannt,
      // dann lieber die gespeicherte Konfiguration unangetastet lassen.
      const ruleset = sets.find(set => set.id === config.rulesetId);
      if (!ruleset) return config;
      const next = this.applyPruefebeneToLevels(config, ruleset);
      if (next !== config) changed = true;
      return next;
    });
    return changed ? migrated : configs;
  }

  /** Migration der Ebenen auf Prüfebene und Prüfmodus des Sets (unveränderte Instanz, wenn gültig). */
  private applyPruefebeneToLevels(config: RulesetReportConfig, ruleset: Ruleset): RulesetReportConfig {
    return migrateLevelsToRuleset(
      config, ruleset.pruefebene, this.effectiveCheckModeOf(ruleset), this.detailColumnsFor(ruleset),
    );
  }

  /** Prüfmodus, wie er sich im Lauf auswirkt (COMBINED nur bei KI-Sets). */
  private effectiveCheckModeOf(ruleset: Ruleset | null | undefined): CheckMode {
    return ruleset ? effectiveCheckMode(ruleset.type, ruleset.checkMode) : 'SINGLE';
  }

  /** KI-Sets: Detail-Spalten dynamisch aus dem benutzerdefinierten Ausgabeformat ableiten. */
  private detailColumnsFor(ruleset: Ruleset): ReportColumnConfig[] | undefined {
    return ruleset.type === 'KI' ? outputParamsToDetailColumns(this.state.aiOutputParameters) : undefined;
  }

  // ─── Regelset-Tabs (Berichtsstruktur pro Set) ─────────────────────────────────

  /**
   * Die ausgewählten myChecks, für die je eine Konfigurationsseite existiert.
   * Auch teilweise ausgewählte Sets (nur einzelne Regeln gewählt) zählen dazu –
   * deren Regeln laufen ebenso in den Bericht.
   */
  get reportRulesets(): Ruleset[] {
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    const ids = [...new Set([
      ...this.state.selectedRuleSetIds,
      ...(this.state.partiallySelectedRuleSetIds ?? []),
    ])];
    return ids
      .map(id => all.find(r => r.id === id))
      .filter((r): r is Ruleset => !!r)
      .sort((a, b) => a.name.localeCompare(b.name, 'de', {numeric: true}));
  }

  get activeRuleset(): Ruleset | undefined {
    const sets = this.reportRulesets;
    if (sets.length === 0) return undefined;
    const idx = Math.min(this.activeRulesetIndex, sets.length - 1);
    return sets[idx];
  }

  selectRulesetTab(index: number): void {
    this.activeRulesetIndex = index;
  }

  /** Perspektive wird nur für KI-/Hybrid-Sets angeboten (IDS hat eine feste Struktur) */
  showsPerspective(ruleset: Ruleset): boolean {
    return ruleset.type === 'KI' || ruleset.type === 'HYBRID';
  }

  getTypeBadgeLabel(type: string): string {
    switch (type) {
      case 'KI':
        return 'KI Prüfung';
      case 'HYBRID':
        return 'Hybrid Prüfung';
      case 'IDS':
        return 'IDS Prüfung';
      default:
        return type;
    }
  }

  /** Beispiel-Vorschau des Dateinamens mit ersetzten Platzhaltern */
  get fileNamePreview(): string {
    const sample: Record<string, string> = {
      '{Checkname}': 'myCheck',
      '{myCheckname}': 'Brandschutz',
      '{Prüfregelname}': 'LPH1',
      '{Projekt}': 'Meinprojekt',
      '{Dateiname}': 'Modell',
      '{Datum}': new Date().toISOString().slice(0, 10),
    };
    const pattern = this.state.reportFileNamePattern?.trim()
      ? this.state.reportFileNamePattern
      : '{Projekt}_{Datum}';
    let result = pattern;
    for (const [token, value] of Object.entries(sample)) {
      result = result.split(token).join(value);
    }
    return `${result}.pdf`;
  }

  // ─── Berichtskonfiguration je Set ─────────────────────────────────────────────

  /**
   * Standard-Berichtsperspektive je myCheck-Typ:
   * - HYBRID: zuerst Dateien, dann Prüfregeln ('files_first')
   * - KI (und sonstige): zuerst Prüfregeln, dann Dateien ('rules_first')
   */
  getDefaultPerspectiveForRuleset(ruleset: Ruleset): ReportPerspective {
    return defaultPerspectiveForRulesetType(ruleset.type);
  }

  get activeConfig(): RulesetReportConfig | null {
    const ruleset = this.activeRuleset;
    return ruleset ? this.getConfig(ruleset) : null;
  }

  /** Gültige Ebenen-Reihenfolgen für das aktive Set (abhängig von dessen Prüfebene). */
  get orderingOptions(): LevelOrdering[] {
    return validLevelOrderings(
      this.activeRuleset?.pruefebene, this.effectiveCheckModeOf(this.activeRuleset),
    );
  }

  /** Ob das aktive Set gemeinsam geprüft wird – dann gibt es keine Dateiebene zur Wahl. */
  get isCombinedCheck(): boolean {
    return this.effectiveCheckModeOf(this.activeRuleset) === 'COMBINED';
  }

  /** Aktuell gewählte Reihenfolge (stabiler Wert) des aktiven Sets. */
  get activeOrderingValue(): string {
    return orderingValueFromLevels(this.activeConfig?.levels);
  }

  updateOrdering(value: string): void {
    const ruleset = this.activeRuleset;
    if (!ruleset) return;
    const ordering = this.orderingOptions.find(o => o.value === value);
    if (!ordering) return;
    const cfg = this.getConfig(ruleset);
    this.upsertConfig({...cfg, levels: createReportLevelsFromOrder(ordering.keys, cfg.levels)});
  }

  /** Fügt eine Konfiguration ein bzw. ersetzt eine bestehende und meldet die Änderung. */
  private upsertConfig(config: RulesetReportConfig): void {
    const configs = this.state.reportConfigs.filter(c => c.rulesetId !== config.rulesetId);
    configs.push(config);
    this.stateChange.emit({reportConfigs: configs});
  }

  // ─── Popup: Anzeigewerte bearbeiten ───────────────────────────────────────────

  openColumnEditor(): void {
    const cfg = this.activeConfig;
    if (!cfg) return;
    const clone = JSON.parse(JSON.stringify(cfg)) as RulesetReportConfig;

    // Struktur-Identifikator: Default = im Set konfigurierter identifikatorParamId, sofern noch nicht gesetzt.
    if (!clone.structureIdentifikatorParamId) {
      clone.structureIdentifikatorParamId = this.activeRuleset?.identifikatorParamId
        ?? this.structureIdentifikatorOptions[0]?.id;
    }
    const identifikatorLabel = this.identifikatorLabelFor(clone.structureIdentifikatorParamId);

    // Spalten je Ebene mit der aktuellen Palette abgleichen (files/rules/structure); detail bleibt dynamisch.
    clone.levels = clone.levels.map(level => ({
      ...level,
      columns: reconcileLevelColumns(level.key, level.columns, identifikatorLabel),
    }));

    this.editingConfig = clone;
    this.editingLevelKey = this.editingConfig.levels[0]?.key ?? '';
    this.showColumnEditor = true;
  }

  // ─── Struktur-Identifikator (welcher Set-Parameter als Identifikator-Spalte dient) ───────

  /** Auswählbare Eingabeparameter des aktiven Sets für die Struktur-Identifikator-Spalte. */
  get structureIdentifikatorOptions(): SetEingabeparameter[] {
    return (this.activeRuleset?.eingabeparameter ?? []).filter(p => (p?.name ?? '').trim().length > 0);
  }

  /** Label (Parametername) zur gegebenen Parameter-ID; Fallback „Identifikator". */
  private identifikatorLabelFor(paramId: string | undefined): string {
    const param = this.structureIdentifikatorOptions.find(p => p.id === paramId);
    return (param?.name ?? '').trim() || 'Identifikator';
  }

  get editingStructureIdentifikatorParamId(): string | undefined {
    return this.editingConfig?.structureIdentifikatorParamId;
  }

  /** Ändert den Struktur-Identifikator: persistiert die Auswahl + relabelt die Identifikator-Spalte. */
  updateStructureIdentifikator(paramId: string): void {
    if (!this.editingConfig) return;
    this.editingConfig.structureIdentifikatorParamId = paramId;
    const label = this.identifikatorLabelFor(paramId);
    const structure = this.editingConfig.levels.find(l => l.key === 'structure');
    const column = structure?.columns.find(c => c.key === STRUCTURE_IDENTIFIKATOR_KEY);
    if (column) column.label = label;
  }

  closeColumnEditor(): void {
    this.showColumnEditor = false;
    this.editingConfig = null;
  }

  applyColumnEditor(): void {
    if (this.editingConfig) {
      this.upsertConfig(this.editingConfig);
    }
    this.closeColumnEditor();
  }

  selectEditingLevel(key: string): void {
    this.editingLevelKey = key;
  }

  get editingLevelColumns() {
    return this.editingConfig?.levels.find(l => l.key === this.editingLevelKey)?.columns ?? [];
  }

  /** Überschrift der aktuell im Popup bearbeiteten Ebene. */
  get editingLevelLabel(): string {
    return this.editingConfig?.levels.find(l => l.key === this.editingLevelKey)?.label ?? '';
  }

  updateEditingLevelLabel(value: string): void {
    const level = this.editingConfig?.levels.find(l => l.key === this.editingLevelKey);
    if (level) level.label = value;
  }

  /** Ob die aktive Ebene per Drag umsortierbar ist (detail = fix). */
  get isEditingLevelReorderable(): boolean {
    return isReportLevelReorderable(this.editingLevelKey);
  }

  /** Ob eine Spalte der aktiven Ebene verschiebbar ist (Status fix an Position 1; detail gar nicht). */
  isColumnDraggable(col: ReportColumnConfig): boolean {
    return isReportColumnDraggable(this.editingLevelKey, col.key);
  }

  /** Ob die Sichtbarkeit einer Spalte gesperrt ist (Status + identifizierende 2. Spalte). */
  isColumnVisibilityLocked(col: ReportColumnConfig): boolean {
    return isReportColumnVisibilityLocked(this.editingLevelKey, col.key);
  }

  dropColumn(event: CdkDragDrop<unknown>): void {
    if (!this.isEditingLevelReorderable) return;
    const cols = this.editingLevelColumns;
    moveItemInArray(cols, event.previousIndex, event.currentIndex);
    // Status bleibt fix an Position 1 – nach jedem Drop erzwingen.
    const statusIndex = cols.findIndex(c => c.key === 'status');
    if (statusIndex > 0) moveItemInArray(cols, statusIndex, 0);
  }

  toggleColumnVisibility(index: number): void {
    const cols = this.editingLevelColumns;
    const col = cols[index];
    if (!col || this.isColumnVisibilityLocked(col)) return;
    col.visible = !col.visible;
  }

  // ─── Ausgabeformate ───────────────────────────────────────────────────────────

  toggleExcelExport(): void {
    this.stateChange.emit({excelExport: !this.state.excelExport});
  }

  togglePdfExport(): void {
    this.stateChange.emit({pdfExport: !this.state.pdfExport});
  }

  toggleBcfExport(): void {
    this.stateChange.emit({bcfExport: !this.state.bcfExport});
  }

  // ─── Verteilung & Ablage ──────────────────────────────────────────────────────

  toggleCreateTask(): void {
    this.stateChange.emit({createTask: !this.state.createTask});
  }

  updateTaskCreationLevel(value: TaskCreationLevel): void {
    this.stateChange.emit({taskCreationLevel: value});
  }

  updateTaskNamePrefix(value: string): void {
    this.stateChange.emit({taskNamePrefix: value});
  }

  toggleStoreInProjectFolder(): void {
    this.stateChange.emit({storeInProjectFolder: !this.state.storeInProjectFolder});
  }

  updateTargetFolder(value: string): void {
    this.stateChange.emit({reportTargetFolder: value});
  }

  updateFileNamePattern(value: string): void {
    this.stateChange.emit({reportFileNamePattern: value});
  }

  /** Fügt einen Platzhalter-Token an das Dateiname-Muster an */
  appendFileNameToken(token: string): void {
    this.stateChange.emit({reportFileNamePattern: (this.state.reportFileNamePattern ?? '') + token});
  }

  /**
   * Liefert die gespeicherte Konfiguration für ein Set oder eine Default-Konfiguration.
   * Passt die Ebenen an die Prüfebene an (3 vs. 4 Spalten): existiert eine gespeicherte
   * Konfiguration mit einer für die Prüfebene ungültigen Reihenfolge (z. B. Altkonfiguration
   * ohne getrennte Strukturebene), wird sie auf eine gültige Default-Reihenfolge migriert
   * (Spalten/Überschriften bleiben erhalten).
   */
  getConfig(ruleset: Ruleset): RulesetReportConfig {
    const existing = this.state.reportConfigs.find(c => c.rulesetId === ruleset.id);
    if (existing) return this.applyPruefebeneToLevels(existing, ruleset);

    // Gemeinsame Ableitung mit dem Direktstart eines Regelsets (createDirectRunState) – so bekommt
    // ein direkt ausgeführtes Set denselben Default-Bericht wie ein im Builder gebauter myCheck.
    return buildDefaultReportConfigForRuleset(
      ruleset.id, ruleset.type, ruleset.pruefebene, this.state.aiOutputParameters, ruleset.checkMode,
    );
  }

  toggleEmailNotification(): void {
    this.stateChange.emit({emailNotification: !this.state.emailNotification});
  }

  updateNotificationMembers(value: number[] | number): void {
    const ids = Array.isArray(value) ? value : value != null ? [value] : [];
    this.stateChange.emit({notificationMemberIds: ids});
  }

  getMemberLabel(user: UserSimpleDto): string {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.mail || `Nutzer ${user.id}`;
  }

  get recipientsString(): string {
    return this.state.recipients.join(', ');
  }

  updateRecipients(value: string): void {
    const recipients = value.split(',').map(r => r.trim()).filter(r => r.length > 0);
    this.stateChange.emit({recipients});
  }
}
